import React, { useRef } from "react";
// NOTE: Assuming you have 'db' and other services correctly set up in this path

const DigitInputs = React.memo(
  ({ digits, setDigits, disabled = false, isPasskey = false }) => {
    const refs = useRef([]);
    // Determines if the input shows characters (text) or dots (password)
    const inputType = isPasskey ? "password" : "text";

    const handleChange = (index, value) => {
      // Allows only one numeric digit
      const numericValue = value.replace(/\D/g, "").slice(0, 1);
      // Prevents non-numeric input from clearing the box
      if (numericValue.length === 0 && value.length > 0) return;

      const newDigits = [...digits];
      newDigits[index] = numericValue;
      setDigits(newDigits);

      // Auto-focus to the next input only if a digit was entered
      if (numericValue && index < 5 && refs.current[index + 1]) {
        refs.current[index + 1].focus();
      }
    };

    const handleKeyDown = (index, e) => {
      // Handle backspace: moves back and clears the previous box if current is empty
      if (
        e.key === "Backspace" &&
        !digits[index] &&
        index > 0 &&
        refs.current[index - 1]
      ) {
        e.preventDefault();
        refs.current[index - 1].focus();
        const newDigits = [...digits];
        newDigits[index - 1] = "";
        setDigits(newDigits);
      }
    };

    return (
      <div
        className="digit-inputs"
        style={{
          display: "flex",
          gap: "clamp(4px, 1.5vw, 8px)",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {digits.map((digit, index) => (
          <input
            key={index}
            type={inputType}
            maxLength="1"
            name={`${isPasskey ? "passkey" : "otp"}-digit-${index}`}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            inputMode="numeric"
            pattern="\d{1}"
            disabled={disabled}
            ref={(el) => (refs.current[index] = el)}
            style={{
              width: "clamp(38px, 9vw, 48px)",
              height: "clamp(42px, 10vw, 50px)",
              textAlign: "center",
              fontSize: "clamp(1.15rem, 3vw, 1.4rem)",
              fontWeight: 700,
              boxSizing: "border-box",
              padding: 0,
              color: "#18181b",
              background: "#ffffff",
              border: "1.5px solid #d4d4d8",
              borderRadius: "6px",
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            }}
          />
        ))}
      </div>
    );
  },
);

// Since DigitInputs is a reusable sub-component, it's good practice to export it.
export { DigitInputs };

// --- Main SecurityStep Component ---
const SecurityStep = React.memo(
  ({
    privatePasskey,
    setPrivatePasskey,
    confirmPasskey,
    setConfirmPasskey,
    errors,
    setErrors,
  }) => {
    // Safely join the array digits into strings for validation
    const fullPrivatePasskey = Array.isArray(privatePasskey)
      ? privatePasskey.join("")
      : "";
    const fullConfirmPasskey = Array.isArray(confirmPasskey)
      ? confirmPasskey.join("")
      : "";

    // --- PASSKEY VALIDATION LOGIC (Effect) ---
    React.useEffect(() => {
      let newErrors = {};

      // Validation for Private Passkey length
      if (fullPrivatePasskey.length > 0 && fullPrivatePasskey.length < 6) {
        newErrors.privatePasskey = "Passkey must be exactly 6 digits.";
      }

      // Validation for Confirm Passkey length
      if (fullConfirmPasskey.length > 0 && fullConfirmPasskey.length < 6) {
        newErrors.confirmPasskey = "Passkey must be exactly 6 digits.";
      }

      // Check for match only if both are full length (6 digits)
      if (fullPrivatePasskey.length === 6 && fullConfirmPasskey.length === 6) {
        if (fullPrivatePasskey !== fullConfirmPasskey) {
          newErrors.passkeysMatch = "Passkeys do not match.";
        }
      }

      setErrors(newErrors);
    }, [fullPrivatePasskey, fullConfirmPasskey, setErrors]);

    // --- COMPONENT RENDER ---
    return (
      <div className="step-content">
        {/* Private Passkey Section - Mandatory Passkey Setup */}
        <div
          className="security-section"
          style={{
            opacity: 1,
            pointerEvents: "auto",
            marginTop: "24px",
          }}
        >
          <h3>Set Private Passkey</h3>
          <div className="form-group" style={{ marginBottom: "20px" }}>
            <label>Private Passkey (6-digits)</label>
            <DigitInputs
              digits={privatePasskey}
              setDigits={setPrivatePasskey}
              isPasskey={true} // Passkey: password input type (dots)
            />
            {errors.privatePasskey && (
              <span
                className="error-message"
                style={{ color: "red", fontSize: "12px" }}
              >
                {errors.privatePasskey}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>Re-enter Private Passkey</label>
            <DigitInputs
              digits={confirmPasskey}
              setDigits={setConfirmPasskey}
              isPasskey={true} // Passkey: password input type (dots)
            />
            {errors.confirmPasskey && (
              <span
                className="error-message"
                style={{ color: "red", fontSize: "12px" }}
              >
                {errors.confirmPasskey}
              </span>
            )}
            {errors.passkeysMatch && (
              <span
                className="error-message"
                style={{ color: "red", fontSize: "12px" }}
              >
                {errors.passkeysMatch}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default SecurityStep;
